import React, { forwardRef, useRef } from 'react';
import { Box, Button, Typography } from '@heycater/design-system';
import truncate from 'lodash/truncate';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import FlexBox from 'shared/components/FlexBox';
import { CONTACT_PHONE } from 'shared/constants';

const TOP_OFFSET = 170;
const PAGE_CONTENT_WIDTH = '720px';
const CTA_IMAGE_NEGATIVE_MARGIN = 8;

const Sidebar = forwardRef(
  (
    {
      title,
      subtitle,
      ctaText,
      url,
      isVisible,
      width = 230,
      rightPadding = '40px',
    },
    ref
  ) => {
    const { t } = useTranslation('static_pages');

    const ctaButtonText = truncate(
      ctaText ? ctaText : t('marketingPages.sidebar.cta'),
      { length: 100 }
    );

    return (
      <CTAWrapperBox
        as="aside"
        ref={ref}
        borderRadius="8px"
        visibility={isVisible ? 'visible' : 'hidden'}
        position="fixed"
        top={TOP_OFFSET + CTA_IMAGE_NEGATIVE_MARGIN * SPACING_MULTIPLIER}
        pb={2}
        left={`calc((100vw - ${PAGE_CONTENT_WIDTH}) / 2 - ${width}px - ${rightPadding})`}
        width={width}
        display={{ xs: 'none', lg: 'block' }}
      >
        <Box mt={-CTA_IMAGE_NEGATIVE_MARGIN} textAlign="center">
          <Image
            alt=""
            src="/artifacts/images/marketing_pages/catering_cta_image.webp"
            height={162}
            width={199}
          />
        </Box>
        <Typography
          variant="h2"
          component="h3"
          align="center"
          px={2}
          mt={2}
          fontSize="24px"
        >
          {title ? title : t('marketingPages.sidebar.title')}
        </Typography>
        <Typography
          variant="body1"
          mt={3}
          px={2}
          align="center"
          fontWeight="500"
          mb={3}
        >
          {subtitle ? subtitle : t('marketingPages.sidebar.body')}
        </Typography>
        <FlexBox
          alignItems="center"
          flexDirection="column"
          px={2}
          mt={2}
          gap={2}
        >
          {url ? (
            <StyledButton
              as="a"
              size="lg"
              rounded
              href={url}
              id="sidebar-cta"
              mt={2}
            >
              {ctaButtonText}
            </StyledButton>
          ) : (
            <StyledButton
              rounded
              size="lg"
              onClick={() => window.scroll({ top: 0, behavior: 'smooth' })}
              id="sidebar-cta"
            >
              {ctaButtonText}
            </StyledButton>
          )}
          <PhoneLink href={CONTACT_PHONE.href}>
            <Typography fontSize="12px">{CONTACT_PHONE.formatted}</Typography>
          </PhoneLink>
        </FlexBox>
      </CTAWrapperBox>
    );
  }
);

Sidebar.displayName = 'Sidebar';

const SPACING_MULTIPLIER = 8;

const PhoneLink = styled.a`
  color: currentColor;
`;

const CTAWrapperBox = styled(Box)`
  background: ${(props) => props.theme.colors.heroGradient};
`;

/**
 * Wraps an area, where the sidebar will be shown.
 */
export const AreaWithSidebarCTA = ({
  children,
  title,
  subtitle,
  ctaText,
  url,
  topPadding = 2,
  bottomPadding = 5,
}) => {
  const { ref: startRef, entry: startEntry } = useInView({ threshold: 1 });
  const { ref: endRef, entry: endEntry } = useInView({ threshold: 1 });

  const startNodePosition = getNodePosition(startEntry);
  const endNodePosition = getNodePosition(endEntry);

  const isBetweenPoints =
    startNodePosition === 'over-viewport' &&
    endNodePosition === 'under-viewport';

  const sidebarRef = useRef(null);
  const sidebarBottom = sidebarRef.current?.getBoundingClientRect()?.bottom;

  const bottomOffset =
    typeof window !== 'undefined' ? window.innerHeight - sidebarBottom : 0;

  return (
    <>
      <Box
        position="relative"
        top={`-${TOP_OFFSET - topPadding * SPACING_MULTIPLIER}px`}
        ref={startRef}
      />
      {children}

      <Sidebar
        ref={sidebarRef}
        isVisible={isBetweenPoints}
        title={title}
        subtitle={subtitle}
        ctaText={ctaText}
        url={url}
      />

      <Box
        position="relative"
        bottom={`-${bottomOffset - bottomPadding * SPACING_MULTIPLIER}px`}
        ref={endRef}
      />
    </>
  );
};

const StyledButton = styled(Button)`
  max-width: 100%;
  font-size: 14px;
  text-decoration: none;
  text-align: center;
  display: block;
  white-space: normal;
`;

function getNodePosition(observerEntry) {
  if (!observerEntry?.rootBounds) {
    return;
  }
  if (observerEntry.isIntersecting) {
    return 'in-viewport';
  } else {
    if (
      observerEntry.boundingClientRect.top > observerEntry.rootBounds.bottom
    ) {
      return 'under-viewport';
    } else {
      return 'over-viewport';
    }
  }
}
